import React, { useState } from 'react';
import { buildApiUrl } from '../config/environment';

import { Plus, Upload, X, FileText } from 'lucide-react';
import ProgressOverlay from '../components/ProgressOverlay';
import { xhrUpload, refreshPresignedUrl } from '../utils/uploadUtils';
import { validateBilingualLessonTitles, getLessonTitleHelperText } from '../utils/videoTitleValidation';

interface Video {
  id: string;
  title: { en: string; tg: string };
  description: { en: string; tg: string };
  file?: File;
  isFreePreview?: boolean;
  duration?: string; // Duration in MM:SS format
  titleErrors?: { en?: string; tg?: string };
}

interface Material {
  id: string;
  title: { en: string; tg: string };
  description: { en: string; tg: string };
  file?: File;
  order: number;
}

interface Course {
  title: { en: string; tg: string };
  description: { en: string; tg: string };
  price: number;
  category: string;
  level: string;
  tags: string[];
  thumbnail?: File;
  videos: Video[];
  materials: Material[];
  hasWhatsappGroup: boolean;
  whatsappGroupLink: string;
}

// Small helper to timeout fetches to avoid infinite pending state
const fetchWithTimeout = async (input: RequestInfo | URL, init: RequestInit & { timeoutMs?: number } = {}) => {
  const { timeoutMs = 60000, ...rest } = init;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(input, { ...rest, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timeout);
  }
};

const AdminUploadPage = () => {
  const [course, setCourse] = useState<Course>({
    title: { en: '', tg: '' },
    description: { en: '', tg: '' },
    price: 0,
    category: '',
    level: '',
    tags: [],
    videos: [],
    materials: [],
    hasWhatsappGroup: false,
    whatsappGroupLink: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [tagsInput, setTagsInput] = useState<string>(''); // Separate state for tags input
  const [error, setError] = useState<string | null>(null); // New state for error messages
  const [videoTitleErrors, setVideoTitleErrors] = useState<{ [key: string]: { en?: string; tg?: string } }>({});

  // Progress overlay state
  const [progressOverlay, setProgressOverlay] = useState({
    isVisible: false,
    progress: 0,
    status: 'loading' as 'loading' | 'success' | 'error',
    title: '',
    message: ''
  });
 
  const addVideo = () => {
    const newVideo: Video = {
      id: Date.now().toString(),
      title: { en: '', tg: '' },
      description: { en: '', tg: '' },
      isFreePreview: false // Default to false
    };
    setCourse(prev => ({
      ...prev,
      videos: [...prev.videos, newVideo]
    }));
  };

  const removeVideo = (videoId: string) => {
    setCourse(prev => ({
      ...prev,
      videos: prev.videos.filter(v => v.id !== videoId)
    }));
  };

  // Handle progress overlay OK button
  const handleProgressOverlayOk = () => {
    setProgressOverlay(prev => ({ ...prev, isVisible: false }));
    
    // If it was a successful upload, redirect to admin courses page
    if (progressOverlay.status === 'success' && progressOverlay.title === 'Course Uploaded Successfully') {
      window.location.href = '/admin/courses';
    }
  };

  const updateVideo = (videoId: string, updates: Partial<Video>) => {
    setCourse(prev => ({
      ...prev,
      videos: prev.videos.map(v => 
        v.id === videoId ? { ...v, ...updates } : v
      )
    }));
    
    // Clear title errors when user starts typing
    if (updates.title?.en || updates.title?.tg) {
      setVideoTitleErrors(prev => {
        const newErrors = { ...prev };
        if (updates.title?.en && newErrors[videoId]?.en) {
          delete newErrors[videoId]?.en;
        }
        if (updates.title?.tg && newErrors[videoId]?.tg) {
          delete newErrors[videoId]?.tg;
        }
        if (!newErrors[videoId]?.en && !newErrors[videoId]?.tg) {
          delete newErrors[videoId];
        }
        return newErrors;
      });
    }
  };

  const addMaterial = () => {
    const newMaterial: Material = {
      id: Date.now().toString(),
      title: { en: '', tg: '' },
      description: { en: '', tg: '' },
      order: course.materials.length + 1
    };
    setCourse(prev => ({
      ...prev,
      materials: [...prev.materials, newMaterial]
    }));
  };

  const removeMaterial = (materialId: string) => {
    setCourse(prev => ({
      ...prev,
      materials: prev.materials.filter(m => m.id !== materialId)
    }));
  };

  const updateMaterial = (materialId: string, updates: Partial<Material>) => {
    setCourse(prev => ({
      ...prev,
      materials: prev.materials.map(m => 
        m.id === materialId ? { ...m, ...updates } : m
      )
    }));
  };

  const handleMaterialUpload = (materialId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      updateMaterial(materialId, { file });
      // Note: Title and description are now required, so we don't auto-fill
    }
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCourse(prev => ({ ...prev, thumbnail: file }));
      console.debug('[UI] thumbnail selected:', file.name, file.size, 'bytes');
    }
  };

  const handleVideoUpload = (videoId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      updateVideo(videoId, { file });
      console.debug('[UI] video selected:', file.name, file.size, 'bytes', 'for id:', videoId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    // Show progress overlay
    setProgressOverlay({
      isVisible: true,
      progress: 0,
      status: 'loading',
      title: 'Uploading Course',
      message: 'Validating course information...'
    });
    
    console.debug('[UI] submit start:', { title: course.title, videos: course.videos.length, hasThumbnail: !!course.thumbnail });
    
    try {
      // Get admin token from localStorage
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        throw new Error('Admin token not found. Please login again.');
      }

      // Update progress - validation
      setProgressOverlay(prev => ({
        ...prev,
        progress: 0,
        message: 'Validating course information...'
      }));

      // Validate required fields with user-friendly messages
      const validationErrors = [];
      
      if (!course.title.en?.trim() || !course.title.tg?.trim()) {
        validationErrors.push('Course title in both English and Tigrinya is required');
      }
      if (!course.description.en?.trim() || !course.description.tg?.trim()) {
        validationErrors.push('Course description in both English and Tigrinya is required');
      }
      if (course.price < 0) {
        validationErrors.push('Price must be a positive number');
      }
      if (!course.category.trim()) {
        validationErrors.push('Course category is required');
      }
      if (!course.level.trim()) {
        validationErrors.push('Course level is required');
      }
      if (!course.thumbnail) {
        validationErrors.push('Course thumbnail is required');
      }
      if (course.videos.length === 0) {
        validationErrors.push('At least one video is required');
      }
      
      // Check each video for required fields
      course.videos.forEach((video, index) => {
        if (!video.title.en?.trim() || !video.title.tg?.trim()) {
          validationErrors.push(`Video ${index + 1}: Title in both English and Tigrinya is required`);
        }
        if (!video.file) {
          validationErrors.push(`Video ${index + 1}: Video file is required`);
        }
        
        // Validate lesson naming convention
        const titleValidation = validateBilingualLessonTitles(video.title.en, video.title.tg);
        if (!titleValidation.isValid) {
          if (titleValidation.errors.en) validationErrors.push(`Video ${index + 1} (English): ${titleValidation.errors.en}`);
          if (titleValidation.errors.tg) validationErrors.push(`Video ${index + 1} (Tigrinya): ${titleValidation.errors.tg}`);
        }
      });
      
      // Check each material for required fields
      course.materials.forEach((material, index) => {
        if (!material.file) {
          validationErrors.push(`Material ${index + 1}: File is required`);
        }
        if (!material.title.en?.trim() || !material.title.tg?.trim()) {
          validationErrors.push(`Material ${index + 1}: Title in both English and Tigrinya is required`);
        }
        if (!material.description.en?.trim() || !material.description.tg?.trim()) {
          validationErrors.push(`Material ${index + 1}: Description in both English and Tigrinya is required`);
        }
      });

      // If there are validation errors, show them and stop
      if (validationErrors.length > 0) {
        setProgressOverlay({
          isVisible: true,
          progress: 100,
          status: 'error',
          title: 'Validation Error',
          message: `Please fix the following errors:\n${validationErrors.join('\n')}`
        });
        setIsLoading(false);
        return;
      }

      // Update progress - preparing upload
      setProgressOverlay(prev => ({
        ...prev,
        progress: 0,
        message: 'Preparing course data for upload...'
      }));

      // Compute total bytes for progress accounting
      const thumbnailBytes = course.thumbnail?.size || 0;
      const videosBytes = course.videos.reduce((sum, v) => sum + (v.file?.size || 0), 0);
      const materialsBytes = course.materials.reduce((sum, m) => sum + (m.file?.size || 0), 0);
      const totalBytesToUpload = thumbnailBytes + videosBytes + materialsBytes;
      let uploadedBytesCompleted = 0;
      console.debug('[UI] bytes:', { thumbnailBytes, videosBytes, totalBytesToUpload });

      // Prepare course data
      const coursePayload = {
        title: course.title,
        description: course.description,
        price: course.price,
        category: course.category,
        level: course.level,
        tags: course.tags,
        hasWhatsappGroup: course.hasWhatsappGroup,
        whatsappGroupLink: course.whatsappGroupLink
      };

      console.debug('[UI] sending course data:', coursePayload);

      // Update progress - creating course
      setProgressOverlay(prev => ({
        ...prev,
        progress: 0,
        message: 'Creating course in database...'
      }));

      // Step 1: Create the course
      const courseResponse = await fetchWithTimeout(buildApiUrl('/api/courses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(coursePayload),
        timeoutMs: 20000
      });
      console.debug('[UI] createCourse status:', courseResponse.status);

      if (!courseResponse.ok) {
        const errorData = await courseResponse.json();
        console.debug('[UI] createCourse error:', errorData);
        throw new Error(errorData.message || 'Failed to create course');
      }

      const courseData = await courseResponse.json();
      console.debug('[UI] course creation response:', courseData);
      const courseId = courseData.data?.courseId || courseData.data?.course?.id || courseData._id;
      console.debug('[UI] created courseId:', courseId);

      if (!courseId) {
        console.error('[UI] No courseId found in response:', courseData);
        throw new Error('Failed to get course ID from server response');
      }

      // Step 2: Upload thumbnail if provided
      if (course.thumbnail && course.thumbnail.name) {
        setProgressOverlay(prev => ({
          ...prev,
          progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
          message: `Uploading thumbnail: ${course.thumbnail?.name || 'thumbnail'}...`
        }));
        
        console.debug('[UI] upload thumbnail start:', course.thumbnail.name);
        const thumbnailFormData = new FormData();
        thumbnailFormData.append('file', course.thumbnail);
        thumbnailFormData.append('version', '1');

        await xhrUpload({
          url: buildApiUrl(`/api/courses/thumbnail/${courseId}`),
          method: 'PUT',
          formData: thumbnailFormData,
          headers: { 'Authorization': `Bearer ${adminToken}` },
          timeoutMs: 2 * 60 * 1000,
          onProgress: (loaded, total) => {
            if (totalBytesToUpload > 0) {
              const currentPercent = Math.round(((uploadedBytesCompleted + loaded) / totalBytesToUpload) * 100);
              setProgressOverlay(prev => ({
                ...prev,
                progress: currentPercent,
                message: `Uploading thumbnail: ${Math.round((loaded / total) * 100)}%`
              }));
            }
            console.debug('[UI] thumb progress:', { loaded, total, currentPercent: Math.round(((uploadedBytesCompleted + loaded) / totalBytesToUpload) * 100) });
          }
        });
        console.debug('[UI] upload thumbnail done');
        uploadedBytesCompleted += thumbnailBytes;
      }

      // Step 3: Upload videos
      for (let i = 0; i < course.videos.length; i++) {
        const video = course.videos[i];
        if (video.file && video.file.name && video.title) {
          setProgressOverlay(prev => ({
            ...prev,
            progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
            message: `Preparing video ${i + 1}/${course.videos.length}: ${video.file?.name || 'video'}...`
          }));
          
          console.debug('[UI] upload video start:', video.file.name);

          let uploadUrl: string;
          let s3Key: string;
          let retryCount = 0;
          const maxRetries = 3;

          // Get presigned URL with retry logic
          while (retryCount <= maxRetries) {
            try {
              const presignedData = await refreshPresignedUrl(
                courseId,
                video.file.name,
                video.file.size,
                video.file.type,
                1, // Default to version 1 for new course uploads
                adminToken
              );
              uploadUrl = presignedData.uploadUrl;
              s3Key = presignedData.s3Key;
              break; // Success, exit retry loop
            } catch (error) {
              retryCount++;
              if (retryCount > maxRetries) {
                throw new Error(`Failed to get upload URL after ${maxRetries} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`);
              }
              console.warn(`Retry ${retryCount}/${maxRetries} for presigned URL:`, error);
              setProgressOverlay(prev => ({
                ...prev,
                message: `Retrying to get upload URL for video ${i + 1}/${course.videos.length} (attempt ${retryCount}/${maxRetries})...`
              }));
              await new Promise(resolve => setTimeout(resolve, 2000 * retryCount)); // Exponential backoff
            }
          }

          setProgressOverlay(prev => ({
            ...prev,
            message: `Uploading video ${i + 1}/${course.videos.length} to cloud storage...`
          }));

          // Upload video with retry logic
          try {
            await xhrUpload({
              url: uploadUrl!,
              method: 'PUT',
              file: video.file!,
              headers: { 'Content-Type': video.file.type },
              timeoutMs: 45 * 60 * 1000, // 45 minutes for large file uploads
              maxRetries: 3,
              onProgress: (loaded, total) => {
                if (totalBytesToUpload > 0) {
                  const currentPercent = Math.round(((uploadedBytesCompleted + loaded) / totalBytesToUpload) * 100);
                  const videoUploadPercent = Math.round((loaded / total) * 100);
                  setProgressOverlay(prev => ({
                    ...prev,
                    progress: currentPercent,
                    message: `Uploading video ${i + 1}/${course.videos.length}: ${videoUploadPercent}%`
                  }));
                }
                console.debug('[UI] video upload progress:', { loaded, total, currentPercent: Math.round(((uploadedBytesCompleted + loaded) / totalBytesToUpload) * 100) });
              }
            });
          } catch (uploadError) {
            const err = uploadError instanceof Error ? uploadError : new Error(String(uploadError));
            console.error(`Video upload failed for ${video.file.name}:`, err);

            // If upload failed due to expired URL, try to refresh and retry once more
            if (err.message === 'PRESIGNED_URL_EXPIRED' && retryCount <= maxRetries) {
              setProgressOverlay(prev => ({
                ...prev,
                message: `Upload URL expired, refreshing for video ${i + 1}/${course.videos.length}...`
              }));
              
              try {
                const freshPresignedData = await refreshPresignedUrl(
                  courseId,
                  video.file.name,
                  video.file.size,
                  video.file.type,
                  1,
                  adminToken
                );
                
                await xhrUpload({
                  url: freshPresignedData.uploadUrl,
                  method: 'PUT',
                  file: video.file!,
                  headers: { 'Content-Type': video.file.type },
                  timeoutMs: 45 * 60 * 1000,
                  maxRetries: 2,
                  onProgress: (loaded, total) => {
                    if (totalBytesToUpload > 0) {
                      const currentPercent = Math.round(((uploadedBytesCompleted + loaded) / totalBytesToUpload) * 100);
                      const videoUploadPercent = Math.round((loaded / total) * 100);
                      setProgressOverlay(prev => ({
                        ...prev,
                        progress: currentPercent,
                        message: `Retrying upload video ${i + 1}/${course.videos.length}: ${videoUploadPercent}%`
                      }));
                    }
                  }
                });
              } catch (retryError) {
                throw new Error(`Upload failed for video ${video.file.name} after URL refresh: ${retryError instanceof Error ? retryError.message : 'Unknown error'}`);
              }
            } else {
              throw new Error(`Upload failed for video ${video.file.name}: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
            }
          }

          console.debug('[UI] video upload done:', video.file.name);

          setProgressOverlay(prev => ({
            ...prev,
            message: `Saving video metadata ${i + 1}/${course.videos.length}...`
          }));

          const metadataResponse = await fetchWithTimeout(buildApiUrl('/api/videos/save-metadata'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${adminToken}`
            },
            body: JSON.stringify({
              courseId,
              title: JSON.stringify(video.title),
              description: JSON.stringify(video.description),
              s3Key: s3Key!,
              order: i + 1,
              duration: video.duration || '00:00',
              fileSize: video.file.size,
              mimeType: video.file.type,
              originalName: video.file.name,
              isFreePreview: video.isFreePreview ? 'true' : 'false'
            }),
            timeoutMs: 5 * 60 * 1000 // 5 minutes for metadata save
          });

          if (!metadataResponse.ok) {
            const errorData = await metadataResponse.json();
            throw new Error(errorData.message || 'Failed to save video metadata');
          }
          console.debug('[UI] upload video done:', video.file.name);
          
          // Show server processing message
          setProgressOverlay(prev => ({
            ...prev,
            message: `Video ${i + 1}/${course.videos.length}: Processing on server...`
          }));
          
          uploadedBytesCompleted += (video.file.size || 0);
          
          // Update progress to show this video is complete
          setProgressOverlay(prev => ({
            ...prev,
            progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
            message: `Video ${i + 1}/${course.videos.length} uploaded successfully!`
          }));
          
          // Add a small delay to show the completion message
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Step 4: Upload materials (optional)
      if (course.materials.length > 0) {
        for (let i = 0; i < course.materials.length; i++) {
          const material = course.materials[i];
          // Check all required fields are present
          if (material.file && material.file.name && 
              material.title.en?.trim() && material.title.tg?.trim() &&
              material.description.en?.trim() && material.description.tg?.trim()) {
            setProgressOverlay(prev => ({
              ...prev,
              progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
              message: `Uploading material ${i + 1}/${course.materials.length}: ${material.file?.name || 'material'}...`
            }));
            
            console.debug('[UI] upload material start:', material.file.name);
            const materialFormData = new FormData();
            materialFormData.append('file', material.file);
            materialFormData.append('courseId', courseId);
            materialFormData.append('version', '1');
            materialFormData.append('title', JSON.stringify({ 
              en: material.title.en.trim(), 
              tg: material.title.tg.trim() 
            }));
            materialFormData.append('description', JSON.stringify({ 
              en: material.description.en.trim(), 
              tg: material.description.tg.trim() 
            }));
            materialFormData.append('order', material.order.toString());

            await xhrUpload({
              url: buildApiUrl('/api/materials/upload'),
              method: 'POST',
              formData: materialFormData,
              headers: { 'Authorization': `Bearer ${adminToken}` },
              timeoutMs: 5 * 60 * 1000, // 5 minutes for materials
              onProgress: (loaded, total) => {
                if (totalBytesToUpload > 0) {
                  const httpUploadPercent = Math.round((loaded / total) * 100);
                  const currentMaterialProgress = Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100);
                  const httpUploadWeight = 0.3;
                  const httpProgress = Math.round((httpUploadPercent / 100) * httpUploadWeight * 100);
                  const totalProgress = Math.min(
                    currentMaterialProgress + httpProgress,
                    currentMaterialProgress + Math.round(httpUploadWeight * 100)
                  );
                  
                  setProgressOverlay(prev => ({
                    ...prev,
                    progress: Math.max(prev.progress, totalProgress),
                    message: `Uploading material ${i + 1}/${course.materials.length}: ${httpUploadPercent}% (Sending to server...)`
                  }));
                }
              }
            });
            console.debug('[UI] upload material done:', material.file.name);
            
            uploadedBytesCompleted += (material.file.size || 0);
            
            setProgressOverlay(prev => ({
              ...prev,
              progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
              message: `Material ${i + 1}/${course.materials.length} uploaded successfully!`
            }));
            
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      // Update progress - finalizing
      setProgressOverlay(prev => ({
        ...prev,
        progress: Math.round((uploadedBytesCompleted / totalBytesToUpload) * 100),
        message: 'Finalizing course upload...'
      }));

      // Update progress - success
      setProgressOverlay({
        isVisible: true,
        progress: 100,
        status: 'success',
        title: 'Course Uploaded Successfully',
        message: 'Your course has been uploaded successfully! You can now manage it from the admin dashboard.'
      });

      console.debug('[UI] upload complete');
      
      try {
        window.dispatchEvent(new Event('course:created'));
      } catch { /* non-critical event dispatch — ignore failures */ }

      // Reset form
      setCourse({
        title: { en: '', tg: '' },
        description: { en: '', tg: '' },
        price: 0,
        category: '',
        level: '',
        tags: [],
        videos: [],
        materials: [],
        hasWhatsappGroup: false,
        whatsappGroupLink: ''
      });
      setTagsInput(''); // Clear tags input

    } catch (error) {
      console.error('Upload error:', error);
      setProgressOverlay({
        isVisible: true,
        progress: 100,
        status: 'error',
        title: 'Upload Failed',
        message: `Error uploading course: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      setError(`Error uploading course: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      console.debug('[UI] submit end');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-20 pb-12">
      <div className={`max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 ${isLoading || progressOverlay.isVisible ? 'blur-[2px] pointer-events-none' : ''}`}>
        <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-700">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">Upload New Course</h1>
            <p className="text-gray-300 mt-2 text-sm sm:text-base">Create and publish your educational content</p>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" noValidate>
            {/* Error Notification */}
            {error && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-orange-300">Please fix the following errors:</h3>
                    <div className="mt-2 text-sm text-orange-200">
                      {error.split('\n').map((line, index) => (
                        <div key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{line}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setError(null)}
                      aria-label="Dismiss error"
                      className="inline-flex text-orange-400 hover:text-orange-300"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Course Basic Info */}
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 border-b border-gray-200 pb-2">
                Course Information
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Title (English) *
                  </label>
                  <input
                    type="text"
                    required
                    value={course.title.en}
                    onChange={(e) => setCourse(prev => ({ ...prev, title: { ...prev.title, en: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="Enter course title in English"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Title (Tigrinya) *
                  </label>
                  <input
                    type="text"
                    required
                    value={course.title.tg}
                    onChange={(e) => setCourse(prev => ({ ...prev, title: { ...prev.title, tg: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="Enter course title in Tigrinya"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Description (English) *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={course.description.en}
                    onChange={(e) => setCourse(prev => ({ ...prev, description: { ...prev.description, en: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="Describe what students will learn in this course (English)"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Description (Tigrinya) *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={course.description.tg}
                    onChange={(e) => setCourse(prev => ({ ...prev, description: { ...prev.description, tg: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="Describe what students will learn in this course (Tigrinya)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Price (CHF) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={course.price}
                    onChange={(e) => setCourse(prev => ({ ...prev, price: Number(e.target.value) }))}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="99.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Category *
                  </label>
                  <select
                    value={course.category}
                    onChange={(e) => setCourse(prev => ({ ...prev, category: e.target.value }))}
                    aria-label="Course category"
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200"
                    required
                  >
                    <option value="">Select a category</option>
                    <option value="crypto">Crypto</option>
                    <option value="investing">Investing</option>
                    <option value="trading">Trading</option>
                    <option value="stock-market">Stock Market</option>
                    <option value="etf">ETF</option>
                    <option value="option-trading">Option Trading</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Level *
                  </label>
                  <select
                    value={course.level}
                    onChange={(e) => setCourse(prev => ({ ...prev, level: e.target.value }))}
                    aria-label="Course level"
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    required
                  >
                    <option value="">Select a level</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Tags (comma-separated, max 3)
                  </label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onBlur={() => {
                      const tags = tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag);
                      // Limit to maximum 3 tags
                      const limitedTags = tags.slice(0, 3);
                      setCourse(prev => ({ ...prev, tags: limitedTags }));
                      // Update input to reflect the limited tags
                      setTagsInput(limitedTags.join(', '));
                    }}
                    onFocus={() => {
                      // Initialize input with current tags when focused
                      if (course.tags.length > 0) {
                        setTagsInput(course.tags.join(', '));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    placeholder="e.g., beginner, advanced, javascript, react"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter tags separated by commas (maximum 3 tags)
                  </p>
                  {tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag).length > 3 && (
                    <p className="mt-1 text-sm text-orange-600">
                      ⚠️ Only the first 3 tags will be saved
                    </p>
                  )}
                  {course.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {course.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* WhatsApp Group Settings */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-white mb-4">WhatsApp Group Settings</h3>
                  
                  <div className="space-y-4">
                    {/* Enable WhatsApp Group */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hasWhatsappGroup"
                        checked={course.hasWhatsappGroup || false}
                        onChange={(e) => setCourse(prev => ({ ...prev, hasWhatsappGroup: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                      />
                      <label htmlFor="hasWhatsappGroup" className="ml-2 block text-sm text-white">
                        Enable WhatsApp Group for this course
                      </label>
                    </div>

                    {/* WhatsApp Group Link */}
                    {course.hasWhatsappGroup && (
                      <div>
                        <label htmlFor="whatsappGroupLink" className="block text-sm font-medium text-gray-300 mb-2">
                          WhatsApp Group Link *
                        </label>
                        <input
                          type="url"
                          id="whatsappGroupLink"
                          value={course.whatsappGroupLink || ''}
                          onChange={(e) => setCourse(prev => ({ ...prev, whatsappGroupLink: e.target.value }))}
                          required={course.hasWhatsappGroup}
                          className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                          placeholder="https://chat.whatsapp.com/your-group-link"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Enter the WhatsApp group invite link. Students will get secure, temporary access to join.
                        </p>
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Security Features
                              </h3>
                              <div className="mt-1 text-sm text-blue-700">
                                <ul className="list-disc list-inside space-y-1">
                                  <li>Links expire in 30 minutes</li>
                                  <li>One-time use only</li>
                                  <li>Only enrolled students can access</li>
                                  <li>Links cannot be shared after use</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Course Thumbnail *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <label
                      htmlFor="thumbnail-upload"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-600 rounded-lg hover:border-cyan-400 transition-colors duration-200 cursor-pointer bg-gray-700 text-gray-300"
                    >
                      <Upload className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        {course.thumbnail ? course.thumbnail.name : 'Upload thumbnail'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Lessons */}
            <div className="space-y-6">

              {/* Free Preview Info Alert */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-300">Free Preview Lessons</h3>
                    <p className="text-sm text-green-200 mt-1">
                      Mark lessons as "Free Preview" to allow users to watch them without purchasing the course. This helps attract potential students by giving them a taste of your content.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-2">
                  Video Lessons
                </h2>
                <button
                  type="button"
                  onClick={addVideo}
                  className="flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-colors duration-200"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Video</span>
                </button>
              </div>

              {course.videos.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No videos added yet. Click "Add Video" to get started.</p>
                </div>
              )}

              {course.videos.map((video, index) => (
                <div key={video.id} className="bg-gray-900 rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Video {index + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => removeVideo(video.id)}
                      aria-label="Remove video"
                      className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video Title (English) *
                    </label>
                    <input
                      type="text"
                      required
                      value={video.title.en}
                      onChange={(e) => updateVideo(video.id, { title: { ...video.title, en: e.target.value } })}
                      className={`w-full px-4 py-3 bg-gray-700 text-white border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400 ${
                        videoTitleErrors[video.id]?.en ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Enter video title in English"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {getLessonTitleHelperText()}
                    </p>
                    {videoTitleErrors[video.id]?.en && (
                      <p className="mt-1 text-xs text-red-400">
                        {videoTitleErrors[video.id]?.en}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video Title (Tigrinya) *
                    </label>
                    <input
                      type="text"
                      required
                      value={video.title.tg}
                      onChange={(e) => updateVideo(video.id, { title: { ...video.title, tg: e.target.value } })}
                      className={`w-full px-4 py-3 bg-gray-700 text-white border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400 ${
                        videoTitleErrors[video.id]?.tg ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Enter video title in Tigrinya"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {getLessonTitleHelperText()}
                    </p>
                    {videoTitleErrors[video.id]?.tg && (
                      <p className="mt-1 text-xs text-red-400">
                        {videoTitleErrors[video.id]?.tg}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Lesson Description (English)
                    </label>
                    <textarea
                      value={video.description.en}
                      onChange={(e) => updateVideo(video.id, { description: { ...video.description, en: e.target.value } })}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Describe what this lesson covers (English)..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Lesson Description (Tigrinya)
                    </label>
                    <textarea
                      value={video.description.tg}
                      onChange={(e) => updateVideo(video.id, { description: { ...video.description, tg: e.target.value } })}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Describe what this lesson covers (Tigrinya)..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video Duration (MM:SS)
                    </label>
                    <input
                      type="text"
                      value={video.duration || ''}
                      onChange={(e) => updateVideo(video.id, { duration: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="e.g., 5:30 or 1:25:45"
                      pattern="^(\d{1,2}:)?[0-5]?\d:[0-5]\d$"
                      title="Format: MM:SS or HH:MM:SS (e.g., 5:30 or 1:25:45)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter duration in MM:SS format (e.g., 5:30) or HH:MM:SS for longer videos
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video File *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleVideoUpload(video.id, e)}
                        className="hidden"
                        id={`video-upload-${video.id}`}
                      />
                      <label
                        htmlFor={`video-upload-${video.id}`}
                        className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-600 rounded-lg hover:border-cyan-400 transition-colors duration-200 cursor-pointer bg-gray-700 text-gray-300"
                      >
                        <Upload className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-gray-600">
                          {video.file ? video.file.name : 'Upload video file'}
                        </span>
                      </label>
                    </div>
                    <p className="mt-2 text-xs text-gray-400">
                      Supported formats: MP4, AVI, MOV, WMV, WebM, OGG, FLV, MKV. Max size: 1GB. Duration must be entered manually. Files are uploaded directly to cloud storage for better performance.
                    </p>
                  </div>

                  {/* Free Preview Toggle */}
                  <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`free-preview-${video.id}`}
                        checked={video.isFreePreview || false}
                        onChange={(e) => updateVideo(video.id, { isFreePreview: e.target.checked })}
                        className="h-5 w-5 text-green-400 focus:ring-green-500 border-gray-600 rounded bg-gray-700"
                      />
                      <div>
                        <label htmlFor={`free-preview-${video.id}`} className="text-sm font-medium text-gray-300 cursor-pointer">
                          Free Preview Lesson
                        </label>
                        <p className="text-xs text-gray-400 mt-1">
                          Allow users to watch this lesson without purchasing the course
                        </p>
                      </div>
                    </div>
                    {video.isFreePreview && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                        🔓 Free Preview
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Course Materials Section */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-2">
                  Course Materials
                </h2>
                <button
                  type="button"
                  onClick={addMaterial}
                  className="flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-colors duration-200"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Material</span>
                </button>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-400 mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-300">Course Materials</h3>
                    <p className="text-sm text-blue-200 mt-1">
                      Upload supplementary materials like PDFs, spreadsheets, worksheets, or documents. These will be available to students who purchase the course.
                    </p>
                    <p className="text-xs text-blue-300 mt-2">
                      Supported formats: PDF, Excel, Word, PowerPoint, CSV, ZIP, images, audio, Python files (Max: 100MB per file)
                    </p>
                  </div>
                </div>
              </div>

              {course.materials.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No materials added yet. Materials are optional.</p>
                </div>
              )}

              {course.materials.map((material, index) => (
                <div key={material.id} className="bg-gray-900 rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Material {index + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => removeMaterial(material.id)}
                      aria-label="Remove material"
                      className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Material Title (English) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={material.title.en}
                      onChange={(e) => updateMaterial(material.id, { title: { ...material.title, en: e.target.value } })}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Enter material title in English"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Material Title (Tigrinya) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={material.title.tg}
                      onChange={(e) => updateMaterial(material.id, { title: { ...material.title, tg: e.target.value } })}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Enter material title in Tigrinya"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description (English) <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={material.description.en}
                      onChange={(e) => updateMaterial(material.id, { description: { ...material.description, en: e.target.value } })}
                      rows={2}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Enter material description in English"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description (Tigrinya) <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={material.description.tg}
                      onChange={(e) => updateMaterial(material.id, { description: { ...material.description, tg: e.target.value } })}
                      rows={2}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                      placeholder="Enter material description in Tigrinya"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Material File <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.txt,.zip,.rar,.json,.png,.jpg,.jpeg,.mp3,.py"
                        onChange={(e) => handleMaterialUpload(material.id, e)}
                        required
                        className="hidden"
                        id={`material-upload-${material.id}`}
                      />
                      <label
                        htmlFor={`material-upload-${material.id}`}
                        className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-600 rounded-lg hover:border-cyan-400 transition-colors duration-200 cursor-pointer bg-gray-700 text-gray-300"
                      >
                        <Upload className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-gray-600">
                          {material.file ? material.file.name : 'Upload material file'}
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Max file size: 100MB
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={material.order}
                      aria-label="Material display order"
                      onChange={(e) => updateMaterial(material.id, { order: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-200 placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers appear first in the materials list
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-3 rounded-lg hover:from-cyan-500 hover:to-blue-500 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </div>
                ) : (
                  'Upload Course'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Progress Overlay */}
      <ProgressOverlay
        isVisible={progressOverlay.isVisible}
        progress={progressOverlay.progress}
        status={progressOverlay.status}
        title={progressOverlay.title}
        message={progressOverlay.message}
        onOk={handleProgressOverlayOk}
      />
    </div>
  );
};

export default AdminUploadPage;